import React from 'react'
import styled from 'styled-components'
import { useHistory, useLocation } from 'react-router-dom'
import { Dialog, DialogContent } from '@material-ui/core'
import { Box } from '../../Shared/Base'
import { SvgIconHeart } from '../../Shared/Svg/Alert'
import { IconChevronLeft, IconChevronRight, IconClose } from '../../Shared/Svg/Icons'
import LinkSafe from '../../Shared/LinkSafe'
import { proxy_image } from '../../../helpers/misc'

const no_pic_url =
  'https://res.cloudinary.com/loueragile/image/upload/v1588422076/web/no-picture.png'

const AlertResultModalPhotos = React.memo(({ image, onClose, onNextImage, onPrevImage }) => {
  const has_image = React.useMemo(() => Boolean(image), [image])
  const handleUserKeyPress = React.useCallback(
    event => {
      const { key } = event

      if (key === 'ArrowRight') {
        onNextImage()
      }

      if (key === 'ArrowLeft') {
        onPrevImage()
      }
    },
    [onNextImage, onPrevImage]
  )

  React.useEffect(() => {
    if (has_image) {
      window.addEventListener('keydown', handleUserKeyPress)

      return () => {
        window.removeEventListener('keydown', handleUserKeyPress)
      }
    }
  }, [handleUserKeyPress, has_image])

  return (
    <Dialog
      fullScreen
      open={has_image}
      PaperProps={{ style: { background: 'white' } }}
      onClose={onClose}
    >
      <Box position="absolute" top={0} right={0} textAlign="right" p={20}>
        <Box as="button" onClick={onClose}>
          <IconClose width={20} height={20} fill="var(--colorLight)" />
        </Box>
      </Box>
      <DialogContent>
        <Box px={20} pt={60}>
          <Box display="flex" position="relative">
            <Box as="button" width={100} color="light" onClick={onPrevImage}>
              <IconChevronLeft width={20} height={20} fill="var(--colorLight)" />
            </Box>
            <Box
              height="calc(100vh - 120px);"
              flex="1"
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ cursor: 'pointer' }}
              onClick={onNextImage}
            >
              <Box
                as="img"
                borderRadius={10}
                src={proxy_image(image)}
                alt="logement"
                style={{ maxHeight: '100%', objectFit: 'cover' }}
                onError={e => {
                  if (e.target.src !== no_pic_url) {
                    e.target.src = no_pic_url
                  }
                }}
              />
            </Box>
            <Box as="button" width={100} color="light" onClick={onNextImage}>
              <IconChevronRight width={20} height={20} fill="var(--colorLight)" />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
})

const Styled = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-top: -2px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 10px;
  overflow: hidden;

  .bg {
    background-color: #efeded;
  }

  .img-rwd {
    border: 5px solid white;
    border-top: 0;
    border-bottom: 0;
    object-fit: cover;
    width: 100%;
    cursor: pointer;
  }

  .borderTop .img-rwd {
    border-top: 10px solid white;
  }

  .col {
    height: 500px;
  }

  .first {
    width: 50%;

    .img-rwd {
      border-left: 0;
    }
  }

  .last {
    .img-rwd {
      border-right: 0;
    }
  }

  .middle,
  .last {
    width: 25%;
  }

  @media screen and (max-width: 1129px) {
    .middle {
      .img-rwd {
        border-right: 0;
      }
    }
  }

  @media screen and (min-height: 1000px) {
    .col {
      height: 500px;
    }
  }

  @media screen and (max-height: 750px) {
    .col {
      height: 350px;
    }
  }

  @media screen and (max-width: 1129px) {
    .col {
      height: 350px;
    }

    .first {
      width: 65%;
    }

    .middle {
      width: 35%;
    }

    .last {
      display: none;
    }
  }
`

const Img = ({ src, onClick }) => (
  <img
    onClick={onClick}
    alt="logement"
    className="img-rwd"
    src={proxy_image(src) || no_pic_url}
    onError={e => {
      if (e.target.src !== no_pic_url) {
        e.target.src = no_pic_url
      }
    }}
  />
)

const AlertResultDesktopTop = ({ linkBack, images, is_favorite, onFavorite, onUnfavorite }) => {
  const history = useHistory()
  const location = useLocation()
  const use_history_back = location.state && location.state.use_history_back
  const [modal_image_index, set_modal_image_index] = React.useState(null)
  const close = React.useCallback(() => set_modal_image_index(null), [set_modal_image_index])
  const nextImage = React.useCallback(
    () => set_modal_image_index(index => (index + 1) % images.length),
    [images.length]
  )
  const prevImage = React.useCallback(
    () => set_modal_image_index(index => (images.length + index - 1) % images.length),
    [images.length]
  )

  return (
    <Styled>
      <Box display="none">
        {images.map((u, i) => (
          <img src={u} key={i} alt="preloader" />
        ))}
      </Box>
      <AlertResultModalPhotos
        onClose={close}
        image={images[modal_image_index]}
        onNextImage={nextImage}
        onPrevImage={prevImage}
      />
      {linkBack && (
        <Box
          as={LinkSafe}
          to={linkBack}
          className="btn is-medium is-white"
          position="absolute"
          display="flex"
          alignItems="center"
          left={30}
          top={30}
          boxShadow={'0px 0px 30px 5px rgba(0,0,0,0.03);'}
          onClick={e => {
            if (use_history_back) {
              e.preventDefault()
              history.goBack()
            }
          }}
        >
          <Box as="span">{use_history_back ? 'Retour' : 'Mon alerte'}</Box>
        </Box>
      )}
      {is_favorite !== undefined && Boolean(is_favorite) && (
        <Box
          onClick={onUnfavorite}
          as="button"
          className="btn is-medium is-white"
          position="absolute"
          display={{ md: 'none', xs: 'flex' }}
          alignItems="center"
          right={30}
          top={30}
          boxShadow={'0px 0px 30px 5px rgba(0,0,0,0.03);'}
        >
          <SvgIconHeart color={'var(--colorNegative)'} />
          <Box as="span" ml={15}>
            Enregistré
          </Box>
        </Box>
      )}
      {is_favorite !== undefined && !Boolean(is_favorite) && (
        <Box
          onClick={onFavorite}
          as="button"
          className="btn is-medium is-white"
          position="absolute"
          display={{ md: 'none', xs: 'flex' }}
          alignItems="center"
          right={30}
          top={30}
          boxShadow={'0px 0px 30px 5px rgba(0,0,0,0.03);'}
        >
          <SvgIconHeart color={'var(--colorThin)'} />
          <Box as="span" ml={15}>
            Enregistrer
          </Box>
        </Box>
      )}
      <Box
        as="button"
        className="btn is-medium is-white"
        position="absolute"
        right={30}
        bottom={30}
        boxShadow={'0px 0px 30px 5px rgba(0,0,0,0.03);'}
        onClick={() => set_modal_image_index(0)}
      >
        Voir les photos
      </Box>
      <Box className="col first bg">
        <Img src={images[0]} onClick={() => set_modal_image_index(0)} />
      </Box>
      <Box className="col middle">
        <Box height="50%" className="bg">
          <Img src={images[1]} onClick={() => set_modal_image_index(1)} />
        </Box>
        <Box height="50%" className="bg borderTop">
          <Img src={images[2]} onClick={() => set_modal_image_index(2)} />
        </Box>
      </Box>
      <Box className="col last">
        <Box height="50%" className="bg">
          <Img src={images[3]} onClick={() => set_modal_image_index(3)} />
        </Box>
        <Box height="50%" className="bg borderTop">
          <Img src={images[4]} onClick={() => set_modal_image_index(4)} />
        </Box>
      </Box>
    </Styled>
  )
}

export default React.memo(AlertResultDesktopTop)
